import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import { GUID } from '@ndvr/auth-api';
import { CreateEventRequest, GlobalAccount } from '@ndvr/customer-api';

import { CustomerApi } from '../../API';
import { Toast } from '../../widgets';
import { useAuthContext } from '../auth/Auth';
import { SuggestedMeetingQueryKeys } from './suggested-meeting-queries';

// Ties a new event back to the note suggestion it came from: the backend marks that suggestion
// converted and useCreateEvent refetches the note's suggestions.
export type MeetingLink = { suggestedMeetingGUID?: string; noteGUID?: string };

export interface CreateEventParams {
    householdGUID: GUID<GlobalAccount>;
    request: CreateEventRequest;
    // Not sent; only names the note whose suggestions to refetch after the create.
    noteGUID?: string;
}

export const useCreateEvent = (options?: UseMutationOptions<void, Error, CreateEventParams>) => {
    const { user } = useAuthContext();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ householdGUID, request }: CreateEventParams): Promise<void> => {
            await CustomerApi({ hideError: true }).scheduleEvent(householdGUID, request);
        },
        onSuccess: (data, variables, onMutateResult, context) => {
            Toast.success('Event created successfully');
            // Invalidate relevant queries that might be affected by the new event
            queryClient.invalidateQueries({
                queryKey: ['events'],
            });
            queryClient.invalidateQueries({
                queryKey: ['personOrg', user?.personOrgGUID, 'calendar'],
            });
            if (variables.noteGUID) {
                queryClient.invalidateQueries({
                    queryKey: SuggestedMeetingQueryKeys.byNote(variables.noteGUID),
                });
            }
            options?.onSuccess?.(data, variables, onMutateResult, context);
        },
        onError: (error: Error, variables, onMutateResult, context) => {
            Toast.forError(error, 'Failed to create event');
            options?.onError?.(error, variables, onMutateResult, context);
        },
        ...options,
    });
};
